import { Button, FormField, Input, Modal, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  errorMessage,
  getConnectorDetailOptions,
  listMyConnectorConnectionsQueryKey,
  setMyConnectorCredentialMutation,
} from "@/api";
import { Text } from "@/components/Text";
import { useConnectorSignIn } from "@/features/build/connectors/useConnectorSignIn";
import type { ConnectorConnectRequest } from "./chatStreamReducer";
import { forgetRetryAfterSignIn, rememberRetryAfterSignIn } from "./useRetryAfterSignIn";

/**
 * The reply needed a connector that takes each person's own token, and I haven't added mine.
 * Saving it here is the same as Settings > Connections; then the chat retries my message.
 */
export function ConnectorConnectDialog({
  request,
  prompt,
  onClose,
  onConnected,
}: Readonly<{ request: ConnectorConnectRequest; prompt?: string; onClose: () => void; onConnected: () => void }>) {
  if (request.authMode === "oauth") return <SignInDialog request={request} prompt={prompt} onClose={onClose} />;
  return <TokenDialog request={request} onClose={onClose} onConnected={onConnected} />;
}

/** A connector people sign in to (phase 6): off to the provider, then back here to resend the message. */
function SignInDialog({
  request,
  prompt,
  onClose,
}: Readonly<{ request: ConnectorConnectRequest; prompt?: string; onClose: () => void }>) {
  const { t } = useTranslation("chat");
  const { signIn, pendingUuid } = useConnectorSignIn();
  const pending = pendingUuid === request.uuid;
  const close = useCallback(() => {
    if (pending) return;
    forgetRetryAfterSignIn();
    onClose();
  }, [pending, onClose]);
  const go = () => {
    if (prompt) rememberRetryAfterSignIn(prompt);
    signIn(request.uuid);
  };
  return (
    <Modal
      open
      onClose={close}
      title={t("connectorConnect.signInTitle", { name: request.name })}
      actions={
        <>
          <Button variant="secondary" onClick={close} disabled={pending}>
            {t("connectorConnect.notNow")}
          </Button>
          <Button variant="primary" loading={pending} onClick={go}>
            {t("connectorConnect.signIn", { name: request.name })}
          </Button>
        </>
      }
    >
      <Text variant="body-sm" tone="secondary">
        {t("connectorConnect.signInBody", { name: request.name })}
      </Text>
    </Modal>
  );
}

function TokenDialog({
  request,
  onClose,
  onConnected,
}: Readonly<{ request: ConnectorConnectRequest; onClose: () => void; onConnected: () => void }>) {
  const { t } = useTranslation("chat");
  const toast = useToast();
  const queryClient = useQueryClient();
  const id = useId();
  const [value, setValue] = useState("");
  const save = useMutation(setMyConnectorCredentialMutation());
  // The owner's setup notes (MHS-875), e.g. where to get a token.
  const notes = useQuery({
    ...getConnectorDetailOptions({ path: { connector_uuid: request.uuid } }),
    select: (r) => r.data.setupNotes?.trim() || null,
  });
  const pending = save.isPending;
  // Modal re-runs its focus effect when onClose changes; keep it stable so typing stays in the field.
  const close = useCallback(() => !pending && onClose(), [pending, onClose]);

  const submit = () =>
    save.mutate(
      { path: { connector_uuid: request.uuid }, body: { value: value.trim() } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: listMyConnectorConnectionsQueryKey() });
          onConnected();
        },
        onError: (error) => toast.error(errorMessage(error, t("connectorConnect.failed", { name: request.name }))),
      },
    );

  return (
    <Modal
      open
      onClose={close}
      title={t("connectorConnect.title", { name: request.name })}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            {t("connectorConnect.notNow")}
          </Button>
          <Button variant="primary" loading={pending} disabled={!value.trim()} onClick={submit}>
            {t("connectorConnect.connect")}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (value.trim()) submit();
        }}
      >
        <Text variant="body-sm" tone="secondary">
          {t("connectorConnect.body", { name: request.name })}
        </Text>
        {notes.data && (
          <Text variant="body-sm" className="whitespace-pre-wrap rounded-lg bg-surface-secondary p-3">
            {notes.data}
          </Text>
        )}
        <FormField label={t("connectorConnect.token")} htmlFor={id}>
          <Input id={id} type="password" autoComplete="off" value={value} onChange={(e) => setValue(e.target.value)} />
        </FormField>
        <Text variant="caption" tone="tertiary">
          {t("connectorConnect.note")}{" "}
          <Link
            to="/settings/connections"
            className="btn-focus-ring rounded text-content-primary underline underline-offset-2"
          >
            {t("connectorConnect.manage")}
          </Link>
        </Text>
      </form>
    </Modal>
  );
}
